import { gql } from '@/core/shared/api';
import { fullBooking, fullBookingStationTime } from './fragments';
import moment from 'moment-timezone';

export const getLocationBookings = async ({ commit, dispatch, rootGetters }, { locationId, startTime, endTime }) => {
  commit('setLoading', true);
  commit('bookings/set', {
    path: 'isFetchingBookings',
    value: true,
  });

  const startTimeFormatted = moment(startTime).set({
    hour: 0,
    minute: 0,
    second: 0,
  }).format();
  const endTimeFormatted = moment(endTime).set({
    hour: 0,
    minute: 0,
    second: 0,
  }).format();

  const query = `query bookingsOnDate ($location: LocationInput, $startTime: String, $endTime: String) {
    bookings (location: $location, startTime: $startTime, endTime: $endTime) {
      ${fullBooking}
    }
  }`;

  try {
    const { bookings } = await gql.request(query, { location: { id: locationId }, startTime: startTimeFormatted, endTime: endTimeFormatted });

    dispatch('insertEntities', {
      name: 'Booking',
      payload: bookings,
    });

    const bookingIdsInApiCall = bookings.map(booking => booking.id);

    // Anything that's in the state, is within the timeboundary but not in this api call, say GOODNIGHT, because you're outta here.
    rootGetters['entities/Booking/all']().forEach(booking => {
      if (moment(booking.startTime).isBetween(startTimeFormatted, endTimeFormatted) && !bookingIdsInApiCall.includes(booking.id)) {
        dispatch('removeEntities', {
          name: 'Booking',
          payload: booking,
        });
      }
    });
  } finally {
    commit('setLoading', false);
    commit('bookings/set', {
      path: 'isFetchingBookings',
      value: false,
    });
  }
};

export const getBooking = async ({ commit, dispatch }, bookingId) => {
  commit('setLoading', true);
  commit('bookings/set', {
    path: 'isFetchingBookings',
    value: true,
  });

  const query = `{
    booking (bookingId: "${bookingId}") {
      ${fullBooking}
    }
  }`;

  try {
    const { booking } = await gql.request(query);
    dispatch('insertEntities', {
      name: 'Booking',
      payload: booking,
    });
    return booking;
  } finally {
    commit('setLoading', false);
    commit('bookings/set', {
      path: 'isFetchingBookings',
      value: false,
    });
  }
};

export const storeBooking = async ({ commit, dispatch, rootState }, booking) => {
  const loadingIds = [];
  if (!booking.id) {
    const createLoadingId = (type = 'booking') => {
      const loadingId = `loading-${type}-${Math.floor(Math.random() * 1000000)}`;
      loadingIds.push(loadingId);
      return loadingId;
    };

    const loadingBooking = { ...booking };
    loadingBooking.bookingStationTimes = loadingBooking.bookingStationTimes.map(bookingStationTime => {
      return { ...bookingStationTime, id: createLoadingId('bookingStationTime') };
    });

    dispatch('insertEntities', {
      name: 'Booking',
      payload: { ...loadingBooking, id: createLoadingId('booking') },
    });
  } else {
    // optimistic update
    dispatch('insertEntities', {
      name: 'Booking',
      payload: { ...booking, _lockWebSocketUpdates: true },
    });
  }

  const mutation = `mutation storeBooking ($booking: BookingInput) {
    storeBooking (booking: $booking) {
      ${fullBooking}
    }
  }`;

  try {
    const { storeBooking } = await gql.request(mutation, { booking }, {
      cancelKey: (booking.id) ? booking.id.split('-').join('') : '',
      throwAbortError: true,
    });

    dispatch('insertEntities', {
      name: 'Booking',
      payload: storeBooking,
    });

    return storeBooking;
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch('getLocationBookings', {
        locationId: rootState.route.params.locationId,
        startTime: rootState.calendarDate,
        endTime: moment(rootState.calendarDate).add(1, 'day').format(),
      });
    }
  } finally {
    // If there was a loading booking, remove it.
    if (loadingIds.length > 0) {
      loadingIds.forEach(loadingId => {
        if (loadingId.includes('bookingStationTime')) {
          dispatch('removeEntities', {
            name: 'BookingStationTime',
            payload: { id: loadingId },
          });
        } else {
          dispatch('removeEntities', {
            name: 'Booking',
            payload: { id: loadingId },
          });
        }
      });
    } else {
      setTimeout(() => {
        dispatch('insertEntities', {
          name: 'Booking',
          payload: { id: booking.id, _lockWebSocketUpdates: false },
        });
      }, 5000);
    }
  }
};

export const deleteBooking = async ({ commit, dispatch, rootState }, booking) => {
  commit('setLoading', true);

  dispatch('removeEntities', {
    name: 'Booking',
    payload: booking,
  });

  const bookingForVars = { id: booking.id };

  const mutation = `mutation deleteBooking ($booking: BookingInput) {
    deleteBooking (booking: $booking) {
      ${fullBooking}
    }
  }`;

  try {
    await gql.request(mutation, { booking: bookingForVars });
  } catch (e) {
    dispatch('getLocationBookings', {
      locationId: booking.location.id,
      startTime: rootState.calendarDate,
      endTime: moment(rootState.calendarDate).add(1, 'day').format(),
    });
  } finally {
    commit('setLoading', false);
  }
};

export const storeBookingStationTime = async ({ commit, dispatch, rootState }, bookingStationTime) => {
  // Optimistic update
  dispatch('insertEntities', {
    name: 'BookingStationTime',
    payload: { ...bookingStationTime, _lockWebSocketUpdates: true },
  });

  const mutation = `mutation storeBookingStationTime ($bookingStationTime: BookingStationTimeInput) {
    storeBookingStationTime (bookingStationTime: $bookingStationTime) {
      ${fullBookingStationTime}
    }
  }`;

  try {
    const { storeBookingStationTime } = await gql.request(mutation, { bookingStationTime });

    dispatch('insertEntities', {
      name: 'BookingStationTime',
      payload: storeBookingStationTime,
    });

    return storeBookingStationTime;
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch('getLocationBookings', {
        locationId: rootState.route.params.locationId,
        startTime: rootState.calendarDate,
        endTime: moment(rootState.calendarDate).add(1, 'day').format(),
      });
    }
  } finally {
    commit('setLoading', false);

    setTimeout(() => {
      dispatch('insertEntities', {
        name: 'BookingStationTime',
        payload: { id: bookingStationTime.id, _lockWebSocketUpdates: false },
      });
    }, 5000);
  }
};

export const addPaidExtension = ({ dispatch, rootGetters }, paidExtension) => {
  const booking = rootGetters['entities/Booking'](paidExtension.bookingId);
  booking.bookingStationTimes.forEach(bookingStationTime => {
    if (paidExtension.fullBooking || bookingStationTime.station.id === paidExtension.stationId) {
      const bookingStationTimeChanges = { id: bookingStationTime.id };

      // If this is being extended after it's already ended, then extend from the currentTime otherwise extend from it's endTime
      if (moment(rootGetters.currentTime).diff(moment(bookingStationTime.endTime)) > 0) {
        bookingStationTimeChanges.endTime = moment(rootGetters.currentTime).add(paidExtension.length, 'minutes').format();
      } else {
        bookingStationTimeChanges.endTime = moment(bookingStationTime.endTime).add(paidExtension.length, 'minutes').format();
      }

      bookingStationTimeChanges.amountPaid = bookingStationTime.amountPaid + paidExtension.amountPaid;
      bookingStationTimeChanges.amountDue = bookingStationTime.amountDue + paidExtension.amountDue;
      bookingStationTimeChanges.customerCard = paidExtension.payment ? { id: paidExtension.payment } : paidExtension.payment;

      dispatch('storeBookingStationTime', bookingStationTimeChanges);
    }
  });
};
